// Contracts — kids background-set rotation, MULTISET model (2026-07-07, Remote
// cluster only). A moderator keeps SEVERAL sets of 3–4 backgrounds on hand and
// CHOOSES which one is the working set; ≤100 finished sessions per working set, then
// a hard gate forces switching to another set (or uploading a new one). The counter
// follows the working set and RESUMES when it's re-selected. Gallery serves the media
// for rotation review (moderator→own+cluster, admin→office, manager→all). Shapes
// snapped from migrations/0006_backgrounds.sql + 0022_backgrounds_multiset.sql.
//
// NOTE: the upload endpoint (POST /api/kids/backgrounds/set) is multipart/form-data
// (file0,file1,file2 required + optional file3) — no JSON request schema. The schemas
// here cover the JSON responses, the gallery query, and the activate/archive requests.
import { z } from 'zod';

// one media item of a set (V1 = photo only; video reserved for a later round).
export const KidsBackgroundMediaSchema = z.object({
  kind: z.enum(['photo', 'video']),
  r2Key: z.string(),
});
export type KidsBackgroundMedia = z.infer<typeof KidsBackgroundMediaSchema>;

// A set's three-state lifecycle (migration 0022): working (counts sessions) ·
// reserve (on hand) · archived (retired, view-only). `isWorkingSet`/`archived` are
// the raw flags; `state` is the derived label the UI groups by. `mine` = the set
// belongs to the calling moderator (only own non-archived sets are activatable).
export const KidsBackgroundSetStateSchema = z.enum(['working', 'reserve', 'archived']);
export type KidsBackgroundSetState = z.infer<typeof KidsBackgroundSetStateSchema>;

export const KidsBackgroundSetSchema = z.object({
  id: z.string(),
  moderatorId: z.string(),
  moderatorName: z.string().nullable(),
  office: z.string().nullable(),
  isWorkingSet: z.boolean(),
  archived: z.boolean(),
  state: KidsBackgroundSetStateSchema,
  mine: z.boolean(),
  sessionCount: z.number(),
  sessionLimit: z.number(),
  // 3 required backgrounds + an optional 4th → 3 or 4 items.
  media: z.array(KidsBackgroundMediaSchema).min(3).max(4),
  createdAt: z.number(),
  archivedAt: z.number().nullable(),
});
export type KidsBackgroundSet = z.infer<typeof KidsBackgroundSetSchema>;

// GET /api/kids/backgrounds/active → the caller's WORKING set (or null) + cluster.
// `gated` = the shot flow is blocked (no working set OR count ≥ limit) for a Remote
// moderator. Atlanta moderators are never gated (feature off → enabled:false).
export const KidsBackgroundsActiveResSchema = z.object({
  enabled: z.boolean(), // feature on for this caller (Remote cluster)
  cluster: z.string().nullable(),
  set: KidsBackgroundSetSchema.nullable(),
  gated: z.boolean(),
});
export type KidsBackgroundsActiveRes = z.infer<typeof KidsBackgroundsActiveResSchema>;

// POST /api/kids/backgrounds/set/activate {setId} → make one of the caller's OWN
// reserve sets the working set (atomically demotes the current working one). The
// chosen set's counter is preserved (it resumes, never resets).
export const KidsBackgroundActivateReqSchema = z.object({
  setId: z.string().trim().min(1).max(80),
});
export type KidsBackgroundActivateReq = z.infer<typeof KidsBackgroundActivateReqSchema>;

export const KidsBackgroundActivateResSchema = z.object({
  ok: z.literal(true),
  set: KidsBackgroundSetSchema,
});
export type KidsBackgroundActivateRes = z.infer<typeof KidsBackgroundActivateResSchema>;

// POST /api/kids/backgrounds/set/archive {setId} → archive one of the caller's OWN
// sets (view-only afterwards; clears is_working_set if it was the working one).
export const KidsBackgroundArchiveReqSchema = z.object({
  setId: z.string().trim().min(1).max(80),
});
export type KidsBackgroundArchiveReq = z.infer<typeof KidsBackgroundArchiveReqSchema>;

export const KidsBackgroundArchiveResSchema = z.object({
  ok: z.literal(true),
});

// POST /api/kids/backgrounds/set/unarchive {setId} → return one of the caller's
// OWN archived sets to service (#45, прецедент 18.07: модератор случайно
// архивнул рабочий сет одним тапом — нужен самостоятельный обратный путь).
// Worker gate: only if the set is NOT exhausted (session_count < session_limit) —
// a full 100/100 set has nothing to return to. Comes back as reserve, or as the
// working set when the moderator has none (atomic CASE in the UPDATE).
export const KidsBackgroundUnarchiveReqSchema = z.object({
  setId: z.string().trim().min(1).max(80),
});
export type KidsBackgroundUnarchiveReq = z.infer<typeof KidsBackgroundUnarchiveReqSchema>;

export const KidsBackgroundUnarchiveResSchema = z.object({
  ok: z.literal(true),
  set: KidsBackgroundSetSchema,
});
export type KidsBackgroundUnarchiveRes = z.infer<typeof KidsBackgroundUnarchiveResSchema>;
export type KidsBackgroundArchiveRes = z.infer<typeof KidsBackgroundArchiveResSchema>;

// GET /api/kids/backgrounds?moderator=&office=&scope= → gallery list (role-scoped).
// `scope` drives the Remote shared gallery: 'all' = every set in the caller's
// cluster (общая галерея — the moderator default), 'mine' = the caller's own sets.
// Atlanta callers never reach the shared gallery (feature off + cluster filter on
// the server), so the cross-moderator view stays Remote-only.
export const KidsBackgroundsListQuerySchema = z.object({
  moderator: z.string().trim().max(80).optional(),
  office: z.string().trim().max(60).optional(),
  scope: z.enum(['all', 'mine']).optional(),
});
export type KidsBackgroundsListQuery = z.infer<typeof KidsBackgroundsListQuerySchema>;

export const KidsBackgroundsListResSchema = z.object({
  sets: z.array(KidsBackgroundSetSchema),
});
export type KidsBackgroundsListRes = z.infer<typeof KidsBackgroundsListResSchema>;

// POST /api/kids/backgrounds/set (multipart) → the new active set.
export const KidsBackgroundSetCreateResSchema = z.object({
  ok: z.literal(true),
  set: KidsBackgroundSetSchema,
});
export type KidsBackgroundSetCreateRes = z.infer<typeof KidsBackgroundSetCreateResSchema>;
